// firebase-auth.jsx — Sign-in only. No self-registration; users are added manually by admin.
import React from 'react';
import { firebase, fbAuth } from './firebase.js';

// Map a Firebase auth error code to a user-facing message. Pure (depends only
// on its argument), so it lives at module scope rather than being rebuilt each render.
const friendly = (code) => {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again later or reset your password.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your administrator.';
    default:
      return 'Sign in failed. Check your credentials and try again.';
  }
};

const EyeIcon = ({ open }) => open ? (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// Self-contained feature glyphs for the brand panel (login loads before the
// shared Icon component, so we keep these inline rather than coupling to it).
const FeatIcon = ({ d }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);

function LoginScreen({ authError, onClearError }) {
  const [email,      setEmail]      = React.useState('');
  const [password,   setPassword]   = React.useState('');
  const [mode,       setMode]       = React.useState('signin'); // 'signin' | 'reset'
  const [error,      setError]      = React.useState(null);
  const [loading,    setLoading]    = React.useState(false);
  const [resetSent,  setResetSent]  = React.useState(false);
  const [showPwd,    setShowPwd]    = React.useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await fbAuth.signInWithEmailAndPassword(email, password);
    } catch (err) {
      setError(friendly(err.code));
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await fbAuth.sendPasswordResetEmail(email);
    } catch (_) {
      // Swallow — don't reveal whether the address is registered
    } finally {
      setLoading(false);
      setResetSent(true);
    }
  };

  const goReset  = () => { setMode('reset');  setError(null); setResetSent(false); };
  const goSignIn = () => { setMode('signin'); setError(null); setResetSent(false); };

  return (
    <div className="lsw">
      {/* ── Brand / value panel (desktop) ─────────────────────────── */}
      <aside className="lsw-hero" aria-hidden="true">
        <div className="lsw-hero-inner">
          <div className="lsw-hero-brand">
            <img src="assets/KCG.webp" alt="" className="lsw-hero-logo" />
            <span className="lsw-hero-name">KCGMatch</span>
          </div>

          <div className="lsw-hero-copy">
            <h1 className="lsw-hero-h">Every invoice,<br/>matched to its quote.</h1>
            <p className="lsw-hero-sub">
              Line-item reconciliation that flags price drift, quantity changes,
              and budget overruns — before they cost you.
            </p>
          </div>

          <ul className="lsw-hero-feats">
            <li>
              <span className="lsw-feat-ic"><FeatIcon d={<><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></>} /></span>
              Auto-pairs invoice lines to quoted items
            </li>
            <li>
              <span className="lsw-feat-ic"><FeatIcon d={<path d="M4 21V4M4 4h12l-2 4 2 4H4"/>} /></span>
              Flags overcharges &amp; quantity changes
            </li>
            <li>
              <span className="lsw-feat-ic"><FeatIcon d={<path d="M12 4 3 20h18z"/>} /></span>
              Tracks budget burn across every job
            </li>
          </ul>

          <div className="lsw-hero-foot">Built for KCG estimating &amp; AP teams</div>
        </div>
      </aside>

      {/* ── Sign-in panel ─────────────────────────────────────────── */}
      <main className="lsw-formpane">
        <div className="lsw-card">
          <div className="lsw-brand lsw-brand-mobile">
            <img src="assets/KCG.webp" alt="" className="brand-logo" />
            <span className="lsw-brand-name">KCGMatch</span>
          </div>

          {mode === 'signin' ? (
            <>
              <div className="lsw-head">
                <div className="lsw-eyebrow">Welcome back</div>
                <h2 className="lsw-title">Sign in to your workspace</h2>
              </div>

              {authError && (
                <div className="lsw-error" role="alert">{authError}</div>
              )}

              <form className="lsw-form" onSubmit={handleSignIn}>
                <div className="lsw-field">
                  <label className="lsw-label" htmlFor="lsw-email">Email</label>
                  <input
                    id="lsw-email" className="lsw-input" type="email"
                    value={email} autoComplete="email" required disabled={loading}
                    autoFocus
                    placeholder="yourname@kcgchicago.com"
                    onChange={(e) => { setEmail(e.target.value); onClearError?.(); }}
                  />
                </div>

                <div className="lsw-field">
                  <div className="lsw-pw-header">
                    <label className="lsw-label" htmlFor="lsw-pwd">Password</label>
                    <button className="lsw-link" type="button" onClick={goReset}>
                      Forgot password?
                    </button>
                  </div>
                  <div className="lsw-pw-wrap">
                    <input
                      id="lsw-pwd" className="lsw-input" type={showPwd ? 'text' : 'password'}
                      value={password} autoComplete="current-password"
                      required disabled={loading}
                      placeholder="••••••••"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button" className="lsw-pw-eye"
                      onClick={() => setShowPwd((p) => !p)}
                      tabIndex={-1}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showPwd} />
                    </button>
                  </div>
                </div>

                {error && <div className="lsw-error" role="alert">{error}</div>}

                <button className="lsw-submit" type="submit" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="lsw-head">
                <div className="lsw-eyebrow">Account</div>
                <h2 className="lsw-title">Reset your password</h2>
              </div>
              <p className="lsw-sub">
                Enter your email and we'll send a reset link if that address is registered.
              </p>

              {resetSent ? (
                <div className="lsw-sent">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7"/>
                  </svg>
                  <span>
                    If <strong>{email}</strong> is registered, a reset link is on its way.
                    Check your inbox (and spam folder).
                  </span>
                </div>
              ) : (
                <form className="lsw-form" onSubmit={handleReset}>
                  <div className="lsw-field">
                    <label className="lsw-label" htmlFor="lsw-reset-email">Email</label>
                    <input
                      id="lsw-reset-email" className="lsw-input" type="email"
                      value={email} autoComplete="email" required disabled={loading}
                      placeholder="you@kcg.com"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  {error && <div className="lsw-error" role="alert">{error}</div>}

                  <button
                    className="lsw-submit" type="submit"
                    disabled={loading || !email.trim()}
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              )}

              <div className="lsw-foot">
                <button className="lsw-link" type="button" onClick={goSignIn}>
                  ← Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      <style>{`
        /* ── Shell: split brand / form layout ──────────────────────── */
        .lsw {
          min-height: 100vh; min-height: 100dvh;
          display: grid; grid-template-columns: 1.05fr 1fr;
          background: var(--bg); color: var(--fg);
        }

        /* ── Brand panel ───────────────────────────────────────────── */
        .lsw-hero {
          position: relative; overflow: hidden;
          background: var(--grad-brand);
          display: flex; align-items: center;
          padding: 48px;
        }
        /* Subtle grid texture so the deep panel doesn't read as flat. */
        .lsw-hero::before {
          content: ""; position: absolute; inset: 0;
          background-image:
            linear-gradient(oklch(1 0 0 / 0.04) 1px, transparent 1px),
            linear-gradient(90deg, oklch(1 0 0 / 0.04) 1px, transparent 1px);
          background-size: 44px 44px;
          mask-image: radial-gradient(ellipse 80% 70% at 30% 30%, #000 30%, transparent 75%);
          pointer-events: none;
        }
        .lsw-hero-inner {
          position: relative; z-index: 1;
          display: flex; flex-direction: column; gap: 36px;
          max-width: 420px;
          color: oklch(0.97 0.01 250);
        }
        .lsw-hero-brand { display: flex; align-items: center; gap: 12px; }
        .lsw-hero-logo {
          width: 34px; height: 34px; object-fit: contain; border-radius: 7px;
          background: oklch(1 0 0 / 0.10); padding: 4px;
        }
        .lsw-hero-name { font-size: 19px; font-weight: 700; letter-spacing: -0.02em; }
        .lsw-hero-copy { display: flex; flex-direction: column; gap: 16px; }
        .lsw-hero-h {
          margin: 0; font-size: 36px; font-weight: 600; line-height: 1.12;
          letter-spacing: -0.025em;
        }
        .lsw-hero-sub {
          margin: 0; font-size: 15px; line-height: 1.6;
          color: oklch(0.86 0.02 250);
        }
        .lsw-hero-feats {
          list-style: none; margin: 0; padding: 0;
          display: flex; flex-direction: column; gap: 14px;
        }
        .lsw-hero-feats li {
          display: flex; align-items: center; gap: 12px;
          font-size: 14px; color: oklch(0.90 0.015 250);
        }
        .lsw-feat-ic {
          flex-shrink: 0; width: 30px; height: 30px; border-radius: 8px;
          display: grid; place-items: center;
          background: oklch(1 0 0 / 0.10);
          border: 1px solid oklch(1 0 0 / 0.14);
          color: oklch(0.95 0.02 250);
        }
        .lsw-hero-foot {
          font-size: 12px; letter-spacing: 0.03em;
          color: oklch(0.72 0.02 250);
          font-family: "IBM Plex Mono", ui-monospace, monospace;
        }

        /* ── Form panel ────────────────────────────────────────────── */
        .lsw-formpane {
          display: flex; align-items: center; justify-content: center;
          padding: 32px 24px;
        }
        .lsw-card {
          width: 100%; max-width: 380px;
          display: flex; flex-direction: column;
        }
        .lsw-brand-mobile { display: none; align-items: center; gap: 10px; margin-bottom: 28px; }
        .lsw-brand-mobile .brand-logo { width: 30px; height: 30px; object-fit: contain; border-radius: 6px; }
        .lsw-brand-name { font-size: 20px; font-weight: 700; color: var(--fg); letter-spacing: -0.02em; }

        .lsw-head { margin-bottom: 28px; }
        /* Page-eyebrow standard: mono + accent + uppercase (matches dashboard,
           vendors, approvals). The house idiom for page kickers. */
        .lsw-eyebrow {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
          color: var(--accent); margin-bottom: 8px;
        }
        .lsw-title {
          margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.02em;
          color: var(--fg);
        }
        .lsw-sub { margin: 0 0 22px; font-size: 13.5px; color: var(--fg-muted); line-height: 1.6; }

        .lsw-form { display: flex; flex-direction: column; gap: 18px; }
        .lsw-field { display: flex; flex-direction: column; gap: 7px; }
        .lsw-label { font-size: 12.5px; font-weight: 600; color: var(--fg-muted); }
        .lsw-pw-header { display: flex; align-items: center; justify-content: space-between; }
        .lsw-pw-wrap { position: relative; }
        .lsw-input {
          appearance: none; width: 100%;
          background: var(--surface-2); color: var(--fg);
          border: 1px solid var(--border); border-radius: var(--r);
          font: inherit; font-size: 16px;            /* 16px: no iOS focus-zoom */
          padding: 0 14px; height: 46px;
          transition: border-color .12s, box-shadow .12s, background .12s;
        }
        .lsw-input::placeholder { color: var(--fg-dim); }
        .lsw-pw-wrap .lsw-input { padding-right: 46px; }
        .lsw-input:focus {
          outline: none;
          border-color: var(--accent);
          background: var(--surface-3);
          box-shadow: 0 0 0 3px oklch(from var(--accent) l c h / 0.30);
        }
        .lsw-pw-eye {
          position: absolute; right: 0; top: 0; bottom: 0;
          width: 46px; display: grid; place-items: center;
          appearance: none; border: 0; background: transparent;
          color: var(--fg-dim); cursor: pointer;
          border-radius: 0 var(--r) var(--r) 0;
          transition: color .12s;
        }
        .lsw-pw-eye:hover { color: var(--fg); }
        .lsw-error {
          background: var(--bad-bg); border: 1px solid oklch(from var(--bad) l calc(c*.5) h / .4);
          color: var(--bad); border-radius: var(--r); padding: 10px 12px;
          font-size: 13px; line-height: 1.5;
        }
        .lsw-submit {
          appearance: none; border: 0; width: 100%; height: 46px;
          background: var(--accent-strong); color: #fff;
          border-radius: var(--r); font: inherit; font-size: 14.5px; font-weight: 600;
          cursor: pointer; letter-spacing: 0.005em;
          transition: filter .12s, transform .06s, box-shadow .12s;
          margin-top: 4px;
          box-shadow: var(--shadow-1);
        }
        .lsw-submit:hover:not(:disabled) { filter: brightness(1.08); box-shadow: var(--shadow-2); }
        .lsw-submit:active:not(:disabled) { transform: translateY(0.5px); }
        .lsw-submit:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
        .lsw-submit:disabled { opacity: 0.55; cursor: not-allowed; }
        .lsw-sent {
          display: flex; align-items: flex-start; gap: 10px;
          background: var(--ok-bg); border: 1px solid oklch(from var(--ok) l calc(c*.5) h / .4);
          color: var(--ok); border-radius: var(--r); padding: 12px 14px;
          font-size: 13px; line-height: 1.55;
        }
        .lsw-sent svg { flex-shrink: 0; margin-top: 1px; }
        .lsw-sent strong { color: inherit; font-weight: 600; }
        .lsw-foot { margin-top: 22px; text-align: center; font-size: 13px; }
        .lsw-link {
          appearance: none; border: 0; background: transparent; padding: 0;
          color: var(--accent); font: inherit; font-size: 12.5px; font-weight: 500;
          cursor: pointer; border-radius: 3px;
        }
        .lsw-link:hover { text-decoration: underline; }
        .lsw-link:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

        /* ── Responsive: collapse to single column ─────────────────── */
        @media (max-width: 880px) {
          .lsw { grid-template-columns: 1fr; }
          .lsw-hero { display: none; }
          .lsw-brand-mobile { display: flex; }
          .lsw-formpane { padding: 28px 20px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lsw-submit, .lsw-input, .lsw-pw-eye, .lsw-link { transition: none; }
        }
      `}</style>
    </div>
  );
}

export { LoginScreen };
